import React, { useEffect } from "react";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm, FormInstance } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../../components/UploadImage";

interface SectionItem {
  title: string;
  description: string[];
}

interface OverviewProps {
  heading?: string;
  subtext?: string;
  image?: string;
  sections?: SectionItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: (values?: any) => void;
  onDelete: (index: number, field: "Overview") => void;
  form: FormInstance;
  overviewData?: OverviewProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const OverviewSection: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  overviewData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!overviewData) return;

    if (mode === "list") {
      form.setFieldsValue({
        overview: {
          heading: overviewData.heading ?? "",
          subtext: overviewData.subtext ?? "",
          image: overviewData.image ? [overviewData.image] : [],
          sections:
            overviewData.sections?.map((s) => ({
              ...s,
              description: s.description?.length ? s.description : [""],
            })) ?? [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        overview: {
          sections: [{ title: "", description: [""] }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = overviewData.sections?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          overview: {
            sections: [
              {
                ...item,
                description: item.description?.length ? item.description : [""],
              },
            ],
          },
        });
      }
    }
  }, [mode, editingIndex, overviewData, form]);

  const sections = overviewData?.sections ?? [];

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["overview", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="Enter heading..." />
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["overview", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} placeholder="Enter subtext..." />
            </Form.Item>

            <Form.Item
              label="Image"
              name={["overview", "image"]}
              rules={[{ required: true, message: "Upload image" }]}
            >
              <UploadImage listType="picture-card" />
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {/* ================= LIST MODE ================= */}
      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Image</th>
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="bg-[#F9F9F9]">
                {overviewData?.heading ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">
                      {overviewData.image && (
                        <Image
                          src={overviewData.image}
                          width={80}
                          height={60}
                          preview={false}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">{overviewData.heading}</td>
                    <td className="px-4 py-3">{overviewData.subtext}</td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                {/* Add Section Button */}
                <tr>
                  {
                    sections?.length < 4 &&  <td colSpan={4} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                    >
                      Add Sections
                    </Button>
                  </td>
                  }
                 
                </tr>

                <tr>
                  <td colSpan={4} className="p-4">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sections?.length ? (
                          sections?.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item.title}</td>
                              <td className="px-4 py-2">
                                {item.description?.join(" ")}
                              </td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Delete this section?"
                                    onConfirm={() =>
                                      onDelete(index, "Overview")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="text-center py-6 text-gray-500"
                            >
                              No sections added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="sm:hidden space-y-4">
            {overviewData?.heading ? (
              <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm">
                <div>
                  <span className="font-semibold text-primary">Heading: </span>
                  <p className="">{overviewData?.heading}</p>
                </div>
                <div>
                  <span className="font-semibold text-primary">Subtext: </span>
                  <div className="line-clamp-2">{overviewData?.subtext}</div>
                </div>

                <div>
                  <span className="font-semibold text-primary block mt-2 mb-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}

            {overviewData?.sections?.length ? (
              overviewData?.sections?.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
                >
                  <p>
                    <span className="font-semibold text-primary">Title: </span>{" "}
                    {item?.title}
                  </p>
                  <p>
                    <span className="font-semibold text-primary">
                      Description:{" "}
                    </span>{" "}
                    <p className="line-clamp-2">{item?.description[0]}</p>
                  </p>
                  <div className="flex justify-end gap-3 mt-2">
                    <EditOutlined
                      className="cursor-pointer text-primary"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Delete this section?"
                      onConfirm={() => onDelete(index, "Overview")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No sections added
              </div>
            )}

            <div className="text-right">
                  {
                    sections?.length < 4 &&  
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                    >
                      Add Sections
                    </Button>
                  
                  }
            </div>
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["overview", "sections"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="p-2 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="Enter title..." />
                      </Form.Item>
                      <Form.List name={[field.name, "description"]}>
                        {(fields, { add, remove }) => {
                          if (fields.length === 0) add();
                          return (
                            <>
                              <div className="flex justify-between items-center mb-2">
                                <div>Description</div>
                                <Button
                                  type="dashed"
                                  icon={<PlusOutlined />}
                                  onClick={() => add()}
                                >
                                  Add Description
                                </Button>
                              </div>

                              {fields.map((field) => (
                                <div
                                  key={field.key}
                                  className="p-2 rounded mb-4"
                                >
                                  <Form.Item
                                    name={field.name}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Enter description",
                                      },
                                    ]}
                                  >
                                    <TextArea
                                      rows={3}
                                      placeholder="Enter description..."
                                    />
                                  </Form.Item>

                                  {fields.length > 1 && (
                                    <div className="flex justify-end">
                                      <MinusOutlined
                                        className="text-red-500 border border-red-500 rounded-full cursor-pointer p-1"
                                        onClick={() => remove(field.name)}
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          );
                        }}
                      </Form.List>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewSection;
